// Das eigene Profil: Bild, Name, E-Mail.
//
// Bewusst getrennt von lib/team.ts: Dort verwaltet der Chef ANDERE Leute (mit
// Rollen und Rechten). Hier verwaltet jeder sich selbst — und darf dabei genau
// die Dinge ändern, die niemandem sonst wehtun.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";

export interface MeinProfil {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  role: string;
  personalnummer: string | null;
}

const SCHLUESSEL = ["mein-profil"];

export function useMeinProfil() {
  const id = useAuth((s) => s.session?.user.id);

  return useQuery({
    queryKey: [...SCHLUESSEL, id],
    enabled: !!id,
    // Der Avatar steht in jeder Kopfzeile — er darf nicht bei jedem
    // Seitenwechsel neu laden und flackern.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url, role, personalnummer")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as MeinProfil;
    },
  });
}

/**
 * Profilbild hochladen.
 *
 * Der Pfad beginnt mit der eigenen Nutzer-ID — daran hängt die Zugriffsregel
 * (Migration 039). Ohne diese Bindung könnte jemand das Bild eines Kollegen
 * überschreiben.
 *
 * Der Dateiname bekommt einen Zeitstempel: Ein fester Name („avatar.jpg") würde
 * zwar aufgeräumter aussehen, aber Browser und CDN cachen ihn — nach dem
 * Wechsel sähe man tagelang das alte Bild und hielte den Upload für kaputt.
 */
export function useAvatarHochladen() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);

  return useMutation({
    mutationFn: async (datei: File) => {
      if (!id) throw new Error("Nicht angemeldet");
      if (!datei.type.startsWith("image/")) {
        throw new Error("Das ist kein Bild. Bitte ein Foto auswählen.");
      }
      // 5 MB — ein Handyfoto liegt darunter, ein versehentlich gewähltes Video
      // darüber. Die Schranke ist da, damit niemand aus Versehen minutenlang
      // hochlädt.
      if (datei.size > 5 * 1024 * 1024) {
        throw new Error("Das Bild ist zu groß (über 5 MB). Bitte ein kleineres wählen.");
      }

      const endung = (datei.name.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "");
      const pfad = `${id}/${Date.now()}.${endung || "jpg"}`;

      const { error: upErr } = await supabase.storage
        .from("avatare")
        .upload(pfad, datei, { cacheControl: "3600", upsert: false });
      if (upErr) throw upErr;

      const url = supabase.storage.from("avatare").getPublicUrl(pfad).data.publicUrl;

      const { error } = await supabase.from("profiles").update({ avatar_url: url }).eq("id", id);
      if (error) throw error;

      return url;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

/** Bild wieder entfernen — zurück zum Anfangsbuchstaben. */
export function useAvatarEntfernen() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);

  return useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Nicht angemeldet");
      const { error } = await supabase.from("profiles").update({ avatar_url: null }).eq("id", id);
      if (error) throw error;
      // Die Datei im Speicher bleibt liegen. Sie zu löschen wäre sauberer, aber
      // ein fehlgeschlagenes Löschen dürfte den Vorgang nicht scheitern lassen —
      // und ein paar verwaiste Bilder kosten nichts.
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

/** Den eigenen Anzeigenamen ändern. */
export function useNamenAendern() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);

  return useMutation({
    mutationFn: async (name: string) => {
      if (!id) throw new Error("Nicht angemeldet");
      const sauber = name.trim();
      if (sauber.length < 2) throw new Error("Der Name ist zu kurz.");

      const { error } = await supabase.from("profiles").update({ full_name: sauber }).eq("id", id);
      if (error) throw error;

      // Auch am Konto hinterlegen: Manche Ansichten lesen den Namen aus der
      // Sitzung statt aus dem Profil — sonst stünden zwei verschiedene da.
      await supabase.auth.updateUser({ data: { full_name: sauber } });
      return sauber;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHLUESSEL }),
  });
}

/**
 * E-Mail-Adresse ändern.
 *
 * ACHTUNG, das ist kein sofortiger Wechsel: Supabase schickt eine
 * Bestätigungsmail an die NEUE Adresse, und erst ein Klick darin schaltet um.
 * Wer das nicht weiß, ändert die Adresse, meldet sich später mit der neuen an
 * und kommt nicht rein. Deshalb sagt die Oberfläche es ausdrücklich.
 */
export function useMailAendern() {
  return useMutation({
    mutationFn: async (neueMail: string) => {
      const mail = neueMail.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
        throw new Error("Das sieht nicht wie eine E-Mail-Adresse aus.");
      }
      const { error } = await supabase.auth.updateUser({ email: mail });
      if (error) throw error;
      return mail;
    },
  });
}
